import { useState, useEffect } from "react";
import { X, Clock, MapPin, Calendar, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import { errMsg } from "@/utils/errMsg";

const API = process.env.REACT_APP_BACKEND_URL;

const locations = [
  { id: "carleton", name: "Carleton Place" },
  { id: "kanata", name: "Kanata" },
  { id: "ottawa", name: "Ottawa" },
];

const treatments = [
  { id: "botox", label: "Botox", labelFr: "Botox" },
  { id: "lip_filler", label: "Lip Filler", labelFr: "Filler Lèvres" },
  { id: "cheek_filler", label: "Cheek Filler", labelFr: "Filler Joues" },
  { id: "dermal_filler", label: "Dermal Filler", labelFr: "Filler Dermique" },
  { id: "jawline", label: "Jawline Contouring", labelFr: "Contour Mâchoire" },
  { id: "sculptra", label: "Sculptra", labelFr: "Sculptra" },
  { id: "microneedling", label: "Microneedling", labelFr: "Microneedling" },
  { id: "prp", label: "PRP / Vampire Facial", labelFr: "PRP / Facial Vampire" },
  { id: "combo", label: "Combo Treatment", labelFr: "Traitement Combo" },
  { id: "skin_treatment", label: "Skin Treatments", labelFr: "Soins de Peau" },
  { id: "consultation", label: "Consultation", labelFr: "Consultation" },
  { id: "other", label: "Other", labelFr: "Autre" },
];

const days = [
  { id: "monday", label: "Monday", labelFr: "Lundi" },
  { id: "tuesday", label: "Tuesday", labelFr: "Mardi" },
  { id: "wednesday", label: "Wednesday", labelFr: "Mercredi" },
  { id: "thursday", label: "Thursday", labelFr: "Jeudi" },
  { id: "friday", label: "Friday", labelFr: "Vendredi" },
  { id: "saturday", label: "Saturday", labelFr: "Samedi" },
];

const times = [
  { id: "morning", label: "Morning (9am-12pm)", labelFr: "Matin (9h-12h)" },
  { id: "afternoon", label: "Afternoon (12pm-4pm)", labelFr: "Après-midi (12h-16h)" },
  { id: "evening", label: "Evening (4pm-7pm)", labelFr: "Soir (16h-19h)" },
];

export default function WaitlistModal({ isOpen, onClose, prefilledTreatment, prefilledLocation }) {
  const { language } = useLanguage();
  const isFr = language === "fr";
  
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [flexibleLocation, setFlexibleLocation] = useState(false);
  const [selectedTreatments, setSelectedTreatments] = useState([]);  // Now an array for multi-select
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [flexibleSchedule, setFlexibleSchedule] = useState(false);
  const [notes, setNotes] = useState("");

  // Iter 172 — prefill treatment & location when the modal is opened
  // from a specific service page (e.g. BotoxOttawaPage → botox + Ottawa).
  // Only applies the prefill on OPEN and only if the user hasn't already
  // selected something in this session.
  useEffect(() => {
    if (!isOpen) return;
    if (prefilledTreatment) {
      const id = String(prefilledTreatment).toLowerCase();
      // Match by id first, then by label (case-insensitive)
      const match = treatments.find(
        (t) => t.id === id || t.label.toLowerCase() === id,
      );
      if (match) {
        setSelectedTreatments((prev) => (prev.length === 0 ? [match.id] : prev));
      }
    }
    if (prefilledLocation) {
      const locId = String(prefilledLocation).toLowerCase();
      const match = locations.find(
        (l) => l.id === locId || l.name.toLowerCase() === locId,
      );
      if (match) {
        setSelectedLocation((prev) => (prev ? prev : match.name));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prefilledTreatment, prefilledLocation]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      setIsVisible(false);
      document.body.style.overflow = "auto";
    }
    return () => { document.body.style.overflow = "auto"; };
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      // Reset form after closing
      if (submitted) {
        setSubmitted(false);
        setName("");
        setEmail("");
        setPhone("");
        setSelectedLocation("");
        setFlexibleLocation(false);
        setSelectedTreatments([]);
        setSelectedDay("");
        setSelectedTime("");
        setFlexibleSchedule(false);
        setNotes("");
      }
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email) {
      toast.error(isFr ? "Nom et email requis" : "Name and email required");
      return;
    }
    if (!selectedLocation && !flexibleLocation) {
      toast.error(isFr ? "Veuillez sélectionner un emplacement" : "Please select a location");
      return;
    }
    if (selectedTreatments.length === 0) {
      toast.error(isFr ? "Veuillez sélectionner au moins un traitement" : "Please select at least one treatment");
      return;
    }

    setIsSubmitting(true);
    
    // Get treatment labels for selected treatments
    const selectedTreatmentLabels = selectedTreatments.map(id => 
      treatments.find(t => t.id === id)?.label || id
    );
    
    try {
      await axios.post(`${API}/api/waitlist/signup`, {
        name,
        email,
        phone,
        preferred_location: selectedLocation || "Any",
        flexible_location: flexibleLocation,
        treatments_wanted: selectedTreatmentLabels,  // Send array of treatment labels
        preferred_day: selectedDay ? days.find(d => d.id === selectedDay)?.label : "",
        preferred_time: selectedTime ? times.find(t => t.id === selectedTime)?.label : "",
        flexible_schedule: flexibleSchedule,
        notes,
      });
      
      setSubmitted(true);
      toast.success(isFr ? "Vous avez été ajouté à la liste d'attente!" : "You've been added to the waitlist!");
    } catch (err) {
      toast.error(errMsg(err, isFr ? "Erreur lors de l'inscription" : "Failed to join waitlist"));
    }
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${isVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
      
      {/* Modal */}
      <div className={`relative w-full max-w-lg bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-700 max-h-[90vh] overflow-y-auto transform transition-all duration-300 ${isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-4"}`}>
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-white text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isFr ? "Rejoindre la Liste d'Attente" : "Join the Waitlist"}
              </h2>
              <p className="text-zinc-400 text-xs">
                {isFr ? "Soyez notifié dès qu'une place se libère" : "Get notified when a spot opens up"}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            /* Success Message */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-white text-xl font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isFr ? "Vous êtes sur la liste!" : "You're on the list!"}
              </h3>
              <p className="text-zinc-400 text-sm mb-6">
                {isFr 
                  ? "Nous vous contacterons dès qu'une place correspondant à vos préférences se libère."
                  : "We'll contact you as soon as a spot matching your preferences opens up."}
              </p>
              <Button onClick={handleClose} className="bg-amber-600 hover:bg-amber-500 text-white">
                {isFr ? "Fermer" : "Close"}
              </Button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Contact Info */}
              <div className="space-y-3">
                <h3 className="text-zinc-300 text-sm font-medium">{isFr ? "Vos informations" : "Your Information"}</h3>
                <Input
                  type="text"
                  placeholder={isFr ? "Votre nom *" : "Your name *"}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  required
                />
                <Input
                  type="email"
                  placeholder={isFr ? "Votre email *" : "Your email *"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  required
                />
                <Input
                  type="tel"
                  placeholder={isFr ? "Téléphone (optionnel)" : "Phone (optional)"}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              {/* Location */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-zinc-300 text-sm font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-400" />
                    {isFr ? "Emplacement préféré" : "Preferred Location"}
                  </h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={flexibleLocation}
                      onChange={(e) => setFlexibleLocation(e.target.checked)}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-zinc-400 text-xs">{isFr ? "Flexible" : "Flexible"}</span>
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {locations.map((loc) => (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => setSelectedLocation(loc.name)}
                      className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                        selectedLocation === loc.name
                          ? "bg-amber-500/20 border-amber-500 text-white"
                          : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                      }`}
                    >
                      {loc.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Treatment - Multi-select */}
              <div className="space-y-3">
                <h3 className="text-zinc-300 text-sm font-medium">
                  {isFr ? "Traitement(s) souhaité(s)" : "Treatment(s) Wanted"}
                  <span className="text-zinc-500 text-xs ml-2">
                    {isFr ? "(sélectionnez tout ce qui s'applique)" : "(select all that apply)"}
                  </span>
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {treatments.map((t) => {
                    const isSelected = selectedTreatments.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setSelectedTreatments(selectedTreatments.filter(id => id !== t.id));
                          } else {
                            setSelectedTreatments([...selectedTreatments, t.id]);
                          }
                        }}
                        className={`p-2.5 rounded-lg border text-xs font-medium transition-all flex items-center gap-2 ${
                          isSelected
                            ? "bg-rose-500/20 border-rose-500 text-white"
                            : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          isSelected ? "bg-rose-500 border-rose-500" : "border-zinc-500"
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        {isFr ? t.labelFr : t.label}
                      </button>
                    );
                  })}
                </div>
                {selectedTreatments.length > 0 && (
                  <p className="text-xs text-amber-400">
                    {selectedTreatments.length} {isFr ? "traitement(s) sélectionné(s)" : "treatment(s) selected"}
                  </p>
                )}
              </div>

              {/* Schedule */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-zinc-300 text-sm font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-400" />
                    {isFr ? "Jour et heure préférés" : "Preferred Day & Time"}
                  </h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={flexibleSchedule}
                      onChange={(e) => setFlexibleSchedule(e.target.checked)}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-zinc-400 text-xs">{isFr ? "Flexible" : "Flexible"}</span>
                  </label>
                </div>
                
                {!flexibleSchedule && (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      {days.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setSelectedDay(d.id === selectedDay ? "" : d.id)}
                          className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                            selectedDay === d.id
                              ? "bg-blue-500/20 border-blue-500 text-white"
                              : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                          }`}
                        >
                          {isFr ? d.labelFr : d.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {times.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setSelectedTime(t.id === selectedTime ? "" : t.id)}
                          className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                            selectedTime === t.id
                              ? "bg-purple-500/20 border-purple-500 text-white"
                              : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                          }`}
                        >
                          {isFr ? t.labelFr : t.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <h3 className="text-zinc-300 text-sm font-medium">{isFr ? "Notes (optionnel)" : "Notes (optional)"}</h3>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={isFr ? "Informations supplémentaires..." : "Any additional information..."}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 resize-none"
                  rows={2}
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 text-white py-6"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  isFr ? "Rejoindre la Liste d'Attente" : "Join the Waitlist"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
